import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Card,
  Upload,
  message,
  Spin,
  Space,
  Checkbox,
  Divider,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  UploadOutlined,
  SyncOutlined,
  ArrowRightOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { recurringExpensesApi } from '../../utils/api';
import { handleFormSubmitError } from '../../utils/errorHandler';
import PersianDatePicker from '../../components/PersianDatePicker';
import { getDefaultCurrencyId } from '../../utils/currencyUtils';
import { formatJalaliApiDate } from '../../utils/dateUtils';

const { Option } = Select;
const { TextArea } = Input;

const rotationOptions = [
  { value: 'daily', label: 'روزانه' },
  { value: 'weekly', label: 'هفتگی' },
  { value: 'bi-weekly', label: 'دو هفته‌ای' },
  { value: 'monthly', label: 'ماهانه' },
  { value: 'quarterly', label: 'سه ماهه' },
  { value: 'half-yearly', label: 'شش ماهه' },
  { value: 'annually', label: 'سالانه' },
];

const AddRecurringExpense = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [billFile, setBillFile] = useState(null);
  const [immediateExpense, setImmediateExpense] = useState(false);

  useEffect(() => {
    fetchCreateData();
  }, []);

  const fetchCreateData = async () => {
    setLoading(true);
    try {
      const res = await recurringExpensesApi.getCreateData();
      if (res?.data) {
        setCurrencies(res.data.currencies || []);
        setEmployees(res.data.employees || []);
        setProjects(res.data.projects || []);
        setCategories(res.data.categories || []);
        setBanks(res.data.banks || []);

        if (res.data.currencies?.length > 0) {
          form.setFieldValue('currency_id', getDefaultCurrencyId(res.data.currencies));
        }
      }
    } catch (error) {
      console.error('Error fetching create data:', error);
      message.error('خطا در دریافت اطلاعات اولیه');
    } finally {
      setLoading(false);
    }
  };

  const handleBillUpload = (info) => {
    if (info.file) {
      setBillFile(info.file);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      let issueDate = null;
      if (immediateExpense) {
        // تاریخ امروز به فرمت شمسی
        issueDate = formatJalaliApiDate(new Date());
      } else if (values.issue_date) {
        issueDate = formatJalaliApiDate(values.issue_date);
      }

      const payload = {
        item_name: values.item_name,
        price: values.price,
        currency_id: values.currency_id,
        category_id: values.category_id || undefined,
        user_id: values.user_id || undefined,
        status: values.status || 'active',
        rotation: values.rotation,
        billing_cycle: values.billing_cycle || undefined,
        description: values.description || undefined,
        purchase_from: values.purchase_from || undefined,
        issue_date: issueDate,
        project_id: values.project_id || undefined,
        bank_account_id: values.bank_account_id || undefined,
        immediate_expense: immediateExpense,
      };

      if (billFile) {
        payload.bill = billFile;
      }

      await recurringExpensesApi.create(payload);
      message.success('هزینه دوره‌ای با موفقیت ثبت شد');
      navigate('/finance/recurring-expenses');
    } catch (error) {
      console.error('Error creating recurring expense:', error);
      handleFormSubmitError(form, error, 'خطا در ثبت هزینه دوره‌ای');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                <SyncOutlined className="text-purple-600 dark:text-purple-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0" style={{ fontFamily: 'CustomFont' }}>
                  ثبت هزینه دوره‌ای جدید
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  اطلاعات هزینه دوره‌ای را تکمیل و ثبت کنید
                </p>
              </div>
            </div>
            <Space>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate('/finance/recurring-expenses')}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
            </Space>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
              initialValues={{
                status: 'active',
                rotation: 'monthly',
                billing_cycle: 1,
              }}
            >
              {/* بخش 1: اطلاعات اصلی هزینه */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <DollarOutlined className="text-green-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>اطلاعات اصلی هزینه</span>
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="item_name"
                    label={<span style={{ fontFamily: 'CustomFont' }}>نام هزینه</span>}
                    rules={[{ required: true, message: 'لطفاً نام هزینه را وارد کنید' }]}
                  >
                    <Input placeholder="مثال: خرید قهوه ماهانه" style={{ fontFamily: 'CustomFont' }} />
                  </Form.Item>

                  <Form.Item
                    name="price"
                    label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>}
                    rules={[{ required: true, message: 'لطفاً مبلغ را وارد کنید' }]}
                  >
                    <InputNumber
                      className="w-full"
                      placeholder="مبلغ هزینه"
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value.replace(/,/g, '')}
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="currency_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>واحد پول</span>}
                    rules={[{ required: true, message: 'لطفاً واحد پول را انتخاب کنید' }]}
                  >
                    <Select placeholder="انتخاب واحد پول" style={{ fontFamily: 'CustomFont' }}>
                      {currencies.map((cur) => (
                        <Option key={cur.id} value={cur.id}>
                          {cur.currency_symbol} ({cur.currency_code})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="purchase_from"
                    label={<span style={{ fontFamily: 'CustomFont' }}>خریداری از</span>}
                  >
                    <Input placeholder="نام فروشنده یا مغازه" style={{ fontFamily: 'CustomFont' }} />
                  </Form.Item>
                </div>
              </Card>

              {/* بخش 2: تنظیمات دوره‌ای */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <SyncOutlined className="text-purple-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>تنظیمات دوره‌ای</span>
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Form.Item
                    name="rotation"
                    label={<span style={{ fontFamily: 'CustomFont' }}>تناوب تکرار</span>}
                    rules={[{ required: true, message: 'لطفاً تناوب تکرار را انتخاب کنید' }]}
                  >
                    <Select placeholder="انتخاب تناوب" style={{ fontFamily: 'CustomFont' }}>
                      {rotationOptions.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="billing_cycle"
                    label={<span style={{ fontFamily: 'CustomFont' }}>دوره صورتحساب</span>}
                  >
                    <InputNumber
                      className="w-full"
                      min={1}
                      placeholder="تعداد دوره"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="status"
                    label={<span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>}
                  >
                    <Select placeholder="انتخاب وضعیت" style={{ fontFamily: 'CustomFont' }}>
                      <Option value="active">فعال</Option>
                      <Option value="inactive">غیرفعال</Option>
                    </Select>
                  </Form.Item>
                </div>
              </Card>

              {/* بخش 3: تاریخ شروع */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <CalendarOutlined className="text-blue-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>تاریخ شروع</span>
                  </div>
                }
              >
                <div className="mb-4">
                  <Checkbox
                    checked={immediateExpense}
                    onChange={(e) => setImmediateExpense(e.target.checked)}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    <span style={{ fontFamily: 'CustomFont' }}>ثبت فوری هزینه (تاریخ امروز)</span>
                  </Checkbox>
                </div>
                {!immediateExpense && (
                  <Form.Item
                    name="issue_date"
                    label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ شروع</span>}
                  >
                    <PersianDatePicker
                      placeholder="انتخاب تاریخ"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>
                )}
              </Card>

              {/* بخش 4: ارجاعات */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <UserOutlined className="text-orange-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>ارجاعات</span>
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="user_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>کارمند</span>}
                  >
                    <Select
                      placeholder="انتخاب کارمند"
                      style={{ fontFamily: 'CustomFont' }}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {employees.map((emp) => (
                        <Option key={emp.id} value={emp.id}>
                          {emp.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="project_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>پروژه</span>}
                  >
                    <Select
                      placeholder="انتخاب پروژه"
                      style={{ fontFamily: 'CustomFont' }}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {projects.map((proj) => (
                        <Option key={proj.id} value={proj.id}>
                          {proj.project_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="category_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>دسته‌بندی هزینه</span>}
                  >
                    <Select placeholder="انتخاب دسته‌بندی" style={{ fontFamily: 'CustomFont' }} allowClear>
                      {categories.map((cat) => (
                        <Option key={cat.id} value={cat.id}>
                          {cat.category_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="bank_account_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>حساب بانکی</span>}
                  >
                    <Select placeholder="انتخاب حساب بانکی" style={{ fontFamily: 'CustomFont' }} allowClear>
                      {banks.map((bank) => (
                        <Option key={bank.id} value={bank.id}>
                          {bank.bank_name || 'صندوق نقدی'}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </Card>

              {/* بخش 5: توضیحات و فایل */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <FileTextOutlined className="text-gray-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>توضیحات و فایل</span>
                  </div>
                }
              >
                <Form.Item
                  name="description"
                  label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}
                >
                  <TextArea rows={3} placeholder="توضیحات اختیاری..." style={{ fontFamily: 'CustomFont' }} />
                </Form.Item>

                <Form.Item
                  name="bill"
                  label={<span style={{ fontFamily: 'CustomFont' }}>فاکتور / رسید</span>}
                >
                  <Upload
                    beforeUpload={() => false}
                    onChange={handleBillUpload}
                    maxCount={1}
                    accept="image/*,.pdf"
                    listType="picture"
                  >
                    <Button icon={<UploadOutlined />} style={{ fontFamily: 'CustomFont' }}>
                      انتخاب فایل
                    </Button>
                  </Upload>
                </Form.Item>
              </Card>

              {/* دکمه‌های عملیات */}
              <div className="flex justify-end gap-4 mt-6">
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => navigate('/finance/recurring-expenses')}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  انصراف
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={submitting}
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ذخیره
                </Button>
              </div>
            </Form>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddRecurringExpense;
